import type { Permission } from '@proerp/shared';
import { useCurrentUser, usePermissions } from '@/lib/useCurrentUser';
import {
  NAV_ITEMS,
  resolveHiddenNavGroups,
  resolveNavGroupOrder,
  type NavGroup,
  type NavItem,
} from './nav';

/**
 * Navigationspunkte gefiltert nach Berechtigung (RBAC, ADR 0011) — auf Haupt-
 * und Unterpunkt-Ebene — UND nach den vom Nutzer selbst ausgeblendeten
 * Navigations-Gruppen (`hiddenNavGroups`, reine Anzeige-Präferenz, KEINE
 * Berechtigung — Direktaufruf/Suche bleiben unberührt), anschließend sortiert
 * nach der vom Nutzer festgelegten Gruppen-Reihenfolge (`navGroupOrder`, siehe
 * `useNavGroupOrder`) — u. a. relevant für BottomNav, deren primäre 4 Tabs die
 * ersten Einträge dieser flachen Liste sind. Punkte ohne `permission` sind
 * immer sichtbar; im Dev-Modus (`*`) bleibt alles sichtbar. Solange die
 * Rechte laden, werden permission-gebundene Punkte ausgeblendet. Ein
 * aufklappbarer Eintrag, dessen Unterpunkte alle gefiltert wurden,
 * verschwindet komplett.
 */
export function useNavItems(): NavItem[] {
  const { can, isLoading } = usePermissions();
  const { data } = useCurrentUser();
  // Gespeicherte Namen können von einer früheren Gliederung stammen (VB-26015) —
  // sie werden auf die heutigen Gruppen umgeschlüsselt, statt wirkungslos zu werden.
  const hiddenGroups = resolveHiddenNavGroups(data?.data.hiddenNavGroups ?? []);
  const groupOrder = resolveNavGroupOrder(data?.data.navGroupOrder ?? []);
  const groupRank = new Map(groupOrder.map((group, index) => [group, index]));
  const isVisible = (permission?: Permission | Permission[]): boolean => {
    if (!permission) return true;
    if (isLoading) return false;
    return Array.isArray(permission) ? permission.some((p) => can(p)) : can(permission);
  };

  const result: NavItem[] = [];
  for (const item of NAV_ITEMS) {
    if (item.hidden) continue;
    if (hiddenGroups.has(item.group)) continue;
    if (!isVisible(item.permission)) continue;
    if (item.children) {
      const children = item.children.filter((child) => !child.hidden && isVisible(child.permission));
      // Container-Eintrag ohne sichtbare Unterpunkte ganz ausblenden.
      if (children.length === 0) continue;
      result.push({ ...item, children });
    } else {
      result.push(item);
    }
  }
  // Array.prototype.sort ist stabil (ES2019+): Reihenfolge innerhalb einer
  // Gruppe bleibt erhalten (NAV_ITEMS ist bereits gruppenweise zusammenhängend).
  return result.slice().sort((a, b) => (groupRank.get(a.group) ?? 0) - (groupRank.get(b.group) ?? 0));
}

/**
 * Vom Nutzer festgelegte Gruppen-Reihenfolge (`navGroupOrder`, reine Anzeige-
 * Präferenz) — AppSidebar/BottomNav iterieren damit statt über die feste
 * NAV_GROUPS-Reihenfolge, um Sidebar-Abschnitte umzusortieren.
 */
export function useNavGroupOrder(): NavGroup[] {
  const { data } = useCurrentUser();
  return resolveNavGroupOrder(data?.data.navGroupOrder ?? []);
}
